/**
 * src/components/importstyle.jsx 
 * FINAL STYLE SNAP & IMPORT: Handles camera, library, and 3D-ready cropping.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import Cropper from 'react-easy-crop';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function ImportStyle({ onClose, onStyleImported }) {
  const [step, setStep] = useState('choose'); // choose | camera | crop | name | processing | done
  const [sourceImg, setSourceImg] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImg, setCroppedImg] = useState(null);
  const [styleName, setStyleName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState({});

  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    if (user) {
      getDoc(doc(db, 'users', user.uid)).then((snap) => {
        if (snap.exists()) setUserData(snap.data());
      });
    }
  }, [user]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
      });
      if (videoRef.current) videoRef.current.srcObject = stream;
      setStep('camera');
    } catch (err) {
      setError('Camera access denied or unavailable');
    }
  };

  const captureFromCamera = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setSourceImg(dataUrl);

    if (video.srcObject) {
        video.srcObject.getTracks().forEach((track) => track.stop());
    }
    setStep('crop');
  };

  const handleLibraryUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setSourceImg(reader.result);
      setStep('crop');
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const finalizeCrop = async () => {
    if (!sourceImg || !croppedAreaPixels) return;
    try {
      const image = new Image();
      image.src = sourceImg;
      await new Promise((resolve) => { image.onload = resolve; });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx.drawImage(
        image, croppedAreaPixels.x, croppedAreaPixels.y,
        croppedAreaPixels.width, croppedAreaPixels.height,
        0, 0, canvas.width, canvas.height
      );

      setCroppedImg(canvas.toDataURL('image/jpeg', 0.92));
      setStep('name');
    } catch (err) {
      setError('Failed to crop image');
    }
  };

  const processStyle = async () => {
    if (!styleName.trim()) { setError('Please name your style'); return; }
    setProcessing(true);

    try {
      const uid = user?.uid;
      
      // 1. ADD TO GLOBAL GALLERY
      const globalRef = await addDoc(collection(db, 'globalGallery'), {
        name: styleName.trim(),
        imageUrl: croppedImg,
        votes: 0,
        sharedBy: userData.name || 'Anonymous',
        createdAt: serverTimestamp()
      });

      // 2. ADD TO PERSONAL VAULT (Sub-collection)
      const personalStyle = {
        id: globalRef.id,
        name: styleName.trim(),
        imageUrl: croppedImg,
        savedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', uid, 'customStyles', globalRef.id), personalStyle);

      setStep('done');
      setTimeout(() => {
        if (onStyleImported) onStyleImported();
        onClose();
      }, 2000);
    } catch (err) {
      setError('Save failed. Check permissions.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        {step === 'choose' && (
          <>
            <h1 style={{ color: GOLD }}>StyleSnap AI</h1>
            <p style={{ opacity: 0.8, marginBottom: '2rem' }}>Import any architecture in seconds</p>
            <button onClick={startCamera} style={btnStyle}>📸 Open Camera</button>
            <label style={outlineBtn}>
              🖼️ From Library
              <input type="file" accept="image/*" onChange={handleLibraryUpload} style={{ display: 'none' }} />
            </label>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', marginTop: '20px' }}>Cancel</button>
          </>
        )}

        {step === 'camera' && (
          <div style={{ width: '100%' }}>
            <video ref={videoRef} autoPlay playsInline style={previewStyle} />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <button onClick={captureFromCamera} style={btnStyle}>Snap Style</button>
          </div>
        )}

        {step === 'crop' && (
          <div style={{ width: '100%', height: '400px', position: 'relative' }}>
            <Cropper
              image={sourceImg} crop={crop} zoom={zoom} aspect={1}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete}
            />
            <button onClick={finalizeCrop} style={{ ...btnStyle, position: 'absolute', bottom: '-80px', left: 0 }}>Confirm Crop</button>
          </div>
        )}

        {step === 'name' && (
          <>
            <p>Name this architecture</p>
            <input 
                style={inputStyle} value={styleName} 
                onChange={e => setStyleName(e.target.value)} 
                placeholder="e.g. Sharp Fade"
            />
            <button onClick={processStyle} disabled={processing} style={btnStyle}>
                {processing ? 'Processing...' : 'Import to Vault'}
            </button>
          </>
        )}

        {step === 'done' && <h2 style={{ color: GOLD }}>Style Synced! 👑</h2>}
        {error && <p style={{ color: '#ff6b6b' }}>{error}</p>}
      </div>
    </div>
  );
}

// Styles
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 10000, display: 'grid', placeItems: 'center', padding: '20px' };
const modalStyle = { background: NAVY, padding: '40px', borderRadius: '30px', border: `2px solid ${GOLD}`, width: '100%', maxWidth: '450px', textAlign: 'center' };
const btnStyle = { width: '100%', padding: '15px', background: GOLD, border: 'none', borderRadius: '50px', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px' };
const outlineBtn = { display: 'block', width: '100%', padding: '15px', border: `1px solid ${GOLD}`, color: GOLD, borderRadius: '50px', cursor: 'pointer', marginTop: '10px' };
const inputStyle = { width: '100%', padding: '15px', borderRadius: '15px', background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', margin: '20px 0' };
const previewStyle = { width: '100%', borderRadius: '20px', marginBottom: '20px', background: '#000' };
      
